import { FunctionComponent, useCallback } from "react"
import { Navigate, Outlet } from "react-router-dom"
import { PageHeader } from "src/global/ui/PageHeader"
import { MainContent, MainContentBlock } from "src/global/layout/MainContent.tsx"
import { DqiFilter } from "src/pages/DataHealthDashboard/components/DqiFilter"
import {
  DataHealthCards,
  DataHealthCardsDatasourcesFragment,
  DataHealthCardsMetricsFragment,
} from "src/pages/DataHealthDashboard/components/DataHealthCards"
import { useDqiFilter } from "src/pages/DataHealthDashboard/hooks/useDqiFilter"
import { useDataHealthEventRefetch } from "src/pages/DataHealthDashboard/hooks/useDataHealthEventRefetch"
import { graphql } from "src/api/graphql/gql"
import { DataQualityIssues } from "src/api/graphql/graphql"
import { FragmentType, unmaskFragment } from "src/api/graphql/fragment-masking"
import { useApolloClient, useQuery } from "@apollo/client"
import { DqiValue } from "src/pages/DataHealthDashboard/constants"
import { DataHealthChartFragment } from "src/pages/DataHealthDashboard/components/DataHealthChart"
import { InactiveAssetsTableFragment } from "src/pages/DataHealthDashboard/components/InactiveAssetsTable/InactiveAssetsTable"

export const DataHealthMetricsQuery = graphql(`
  query DataHealthMetrics($input: DataHealthInput) {
    dataHealth(input: $input) {
      averageDailyScore
      ...DataHealthCardsMetrics
      ...DataHealthChart
    }
  }
`)

export const CoverageQuery = graphql(`
  query Coverage {
    coverage {
      ...DataHealthCardsCoverage
    }
  }
`)

export const DatasourcesQuery = graphql(`
  query DatasourcesForDataHealth {
    datasourcesV2 {
      ...DataHealthCardsDatasources
      ...InactiveAssetsTable
    }
  }
`)

export interface DataHealthDashboardOutletContext {
  dataHealth:
    | (FragmentType<typeof DataHealthCardsMetricsFragment> & FragmentType<typeof DataHealthChartFragment>)
    | null
    | undefined
  datasources:
    | (FragmentType<typeof DataHealthCardsDatasourcesFragment> & FragmentType<typeof InactiveAssetsTableFragment>)[]
    | null
    | undefined
  dataHealthLoading: boolean
  datasourcesLoading: boolean
  dqi: DqiValue
}
export const DataHealthDashboard: FunctionComponent = () => {
  const { dqi, gqlDQI, setDqi } = useDqiFilter()
  const { cache } = useApolloClient()

  const dataHealthQuery = useQuery(DataHealthMetricsQuery, {
    variables: { input: gqlDQI ? { dqi: gqlDQI as DataQualityIssues } : null },
  })

  const coverageQuery = useQuery(CoverageQuery)

  const datasourcesQuery = useQuery(DatasourcesQuery)

  const refetchAll = useCallback(async () => {
    const datasources = datasourcesQuery.data?.datasourcesV2 ?? []
    cache.batch({
      update(cache) {
        cache.evict({ fieldName: "dataHealth" })
        cache.evict({ fieldName: "coverage" })
        for (const masked of datasources) {
          const ds = unmaskFragment(DataHealthCardsDatasourcesFragment, masked)
          if (ds?.id) {
            cache.evict({ id: `${ds.__typename}:${ds.id}` })
          }
        }
      },
      optimistic: false,
    })
    cache.gc()
    await Promise.all([dataHealthQuery.refetch(), coverageQuery.refetch(), datasourcesQuery.refetch()])
  }, [dataHealthQuery, coverageQuery, datasourcesQuery, cache])

  useDataHealthEventRefetch(refetchAll)

  if (!dataHealthQuery.loading && dataHealthQuery.data?.dataHealth === null) {
    return <Navigate to="/data-assets" replace />
  }

  return (
    <PageHeader
      headerContent={{
        noBackButton: true,
        title: "Data Health",
      }}
    >
      <MainContent>
        <MainContentBlock size="large">
          <DqiFilter value={dqi} onChange={setDqi} />
          <DataHealthCards
            dataHealth={dataHealthQuery.data?.dataHealth}
            coverage={coverageQuery.data?.coverage}
            datasources={datasourcesQuery.data?.datasourcesV2}
            dataHealthLoading={dataHealthQuery.loading}
            coverageLoading={coverageQuery.loading}
            datasourcesLoading={datasourcesQuery.loading}
            dataHealthError={dataHealthQuery.error}
            coverageError={coverageQuery.error}
            datasourcesError={datasourcesQuery.error}
            dqi={dqi}
          />
          <Outlet
            context={
              {
                dataHealth: dataHealthQuery.data?.dataHealth,
                datasources: datasourcesQuery.data?.datasourcesV2,
                dataHealthLoading: dataHealthQuery.loading,
                datasourcesLoading: datasourcesQuery.loading,
                dqi,
              } satisfies DataHealthDashboardOutletContext
            }
          />
        </MainContentBlock>
      </MainContent>
    </PageHeader>
  )
}
